import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, Phone, Trophy, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import logoFull from '@/assets/logo-full-white-transparent.png';

const navLinks = [
  { name: 'Case Studies', href: '/case-studies' },
  { name: 'The Team', href: '/#team' },
  { name: 'Blog', href: '/blog' },
  { name: 'Resources', href: '/resources' },
];

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href: string) => {
    setIsMobileMenuOpen(false);
    if (href.startsWith('/#') && location.pathname === '/') {
      const el = document.querySelector(href.replace('/', ''));
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className="fixed top-4 left-0 right-0 z-50 flex justify-center px-4"
    >
      <div className="w-auto max-w-full">
        <div className="flex items-center gap-6 h-16 pl-5 pr-2 rounded-full bg-background/80 backdrop-blur-xl border border-white/15 shadow-2xl">
          <Link to="/" className="flex items-center shrink-0" aria-label="The Alpha Omega Hub">
            <span
              className="block h-10 md:h-11 w-24 bg-primary"
              style={{
                WebkitMaskImage: `url(${logoFull})`,
                maskImage: `url(${logoFull})`,
                WebkitMaskRepeat: 'no-repeat',
                maskRepeat: 'no-repeat',
                WebkitMaskPosition: 'center',
                maskPosition: 'center',
                WebkitMaskSize: 'contain',
                maskSize: 'contain',
              }}
            />
          </Link>

          <div className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                onClick={() => handleNavClick(link.href)}
                className="text-foreground/85 hover:text-foreground font-semibold text-base transition-colors whitespace-nowrap"
              >
                {link.name === 'Case Studies' && <Trophy className="mr-1.5 inline-block h-4 w-4 text-accent" aria-hidden="true" />}
                {link.name}
              </Link>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-3">
            <a
              href="tel:+18138193701"
              className="inline-flex items-center gap-2 !rounded-full border-2 border-accent bg-transparent px-5 py-3 text-base font-semibold text-foreground whitespace-nowrap transition-all duration-300 hover:bg-accent/10 hover:scale-105"
            >
              <Phone className="h-4 w-4 text-accent" aria-hidden="true" />
              +1 (813) 819-3701
            </a>
            <Link to="/#contact" className="btn-hero-primary text-base py-3 px-6 !rounded-full">
              Get in contact
            </Link>
          </div>

          <div className="lg:hidden flex items-center gap-1.5">
            <a
              href="tel:+18138193701"
              aria-label="Call The Alpha Omega Hub at (813) 819-3701"
              className="inline-flex items-center gap-1.5 !rounded-full border-2 border-accent bg-transparent px-3 py-2 text-sm font-semibold text-foreground whitespace-nowrap transition-all duration-300 active:scale-95"
            >
              <Phone className="h-4 w-4 shrink-0 text-accent" aria-hidden="true" />
              <span className="min-[360px]:hidden">Call</span>
              <span className="hidden min-[360px]:inline">(813) 819-3701</span>
            </a>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMobileMenuOpen}
              className="p-2 text-foreground min-h-11 min-w-11 flex items-center justify-center"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="lg:hidden absolute top-20 left-4 right-4 rounded-2xl bg-background/95 backdrop-blur-xl border border-white/15 shadow-2xl"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  onClick={() => handleNavClick(link.href)}
                  className="text-lg text-foreground/80 hover:text-foreground py-2"
                >
                  {link.name === 'Case Studies' && <Trophy className="mr-2 inline-block h-5 w-5 text-accent" aria-hidden="true" />}
                  {link.name}
                </Link>
              ))}
              <Link to="/#contact" onClick={() => setIsMobileMenuOpen(false)} className="btn-hero-primary text-center mt-2">
                Get in contact
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>

    </>
  );
};
